import type { PublicGuest, PublicState } from '@karozik/shared';
import { Artwork, Avatar, cn, Equalizer } from '@karozik/ui';

const COLORS = ['bg-pink', 'bg-violet', 'bg-cyan text-night', 'bg-sun text-night'];
const LETTERS = ['A', 'B', 'C', 'D'];

export function BlindtestScreen({ state, now }: { state: PublicState; now: number }) {
  const bt = state.blindtest!;
  const left = Math.min(bt.stage === 'playing' ? 20 : 99, Math.max(0, Math.ceil((bt.stageEndsAt - now) / 1000)));
  const token = state.event.joinUrl.split('/j/')[1];

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-night px-[4vw] py-[4vh]">
      <header className="flex items-center justify-between">
        <div className="font-display text-[5vh] font-bold text-gradient">🎧 Blind test</div>
        {bt.round > 0 && bt.stage !== 'final' && (
          <div className="text-[3vh] text-ink-dim">
            Manche <b className="text-ink">{bt.round}</b> / {bt.totalRounds}
          </div>
        )}
      </header>

      {bt.stage === 'intro' && (
        <div className="flex flex-1 items-center justify-center gap-[5vw]">
          <div>
            <div className="font-display text-[9vh] leading-tight font-bold">
              Sortez vos
              <br />
              téléphones !
            </div>
            <p className="mt-[2vh] max-w-[40vw] text-[3vh] text-ink-dim">Un extrait, quatre réponses : soyez le plus rapide pour marquer un max de points.</p>
            <div className="font-display mt-[4vh] text-[14vh] font-bold">{left}</div>
          </div>
          <img src={`/api/join/${token}/qr.svg`} alt="" className="h-[40vh] w-[40vh] rounded-[3vh] bg-white p-[1.5vh]" />
        </div>
      )}

      {bt.stage === 'playing' && bt.choices && (
        <div className="flex flex-1 flex-col">
          <div className="mt-[3vh] flex items-center gap-[2vw]">
            <Equalizer className="h-[6vh] text-pink" bars={7} />
            <div className="h-[2vh] flex-1 overflow-hidden rounded-full bg-white/10">
              <div className="bg-gradient-neon h-full transition-all duration-500" style={{ width: `${(left / 20) * 100}%` }} />
            </div>
            <div className="font-display w-[8vw] text-right text-[7vh] font-bold">{left}</div>
          </div>
          <div className="mt-[4vh] grid flex-1 grid-cols-2 gap-[2vw]">
            {bt.choices.map((c, i) => (
              <div key={i} className={cn('flex items-center gap-[1.5vw] rounded-[3vh] px-[2.5vw] text-[4.2vh] leading-tight font-bold', COLORS[i])}>
                <span className="font-display text-[7vh] opacity-60">{LETTERS[i]}</span>
                {c}
              </div>
            ))}
          </div>
          <div className="mt-[3vh] text-center text-[3vh] text-ink-dim">
            {bt.answered.length} réponse{bt.answered.length > 1 ? 's' : ''}
          </div>
        </div>
      )}

      {bt.stage === 'reveal' && (
        <div className="flex flex-1 items-center gap-[4vw]">
          {bt.revealTrack && <Artwork track={bt.revealTrack} className="h-[55vh] w-[55vh]" rounded="rounded-[3vh]" />}
          <div className="min-w-0 flex-1">
            <div className="text-[3vh] font-semibold text-lime">C’était…</div>
            <div className="font-display text-[7vh] leading-tight font-bold">{bt.revealTrack?.title}</div>
            <div className="text-[4vh] text-ink-dim">{bt.revealTrack?.artist}</div>
            <RoundWinners leaderboard={bt.leaderboard} lastPoints={bt.lastPoints} />
          </div>
        </div>
      )}

      {bt.stage === 'final' && <Podium board={bt.leaderboard} />}
    </div>
  );
}

type Board = { guest: PublicGuest; points: number }[];

/** Who scored in the round that just ended (blind test and Têtes Mêlées). */
export function RoundWinners({ leaderboard, lastPoints }: { leaderboard: Board; lastPoints: Record<string, number> }) {
  const byId = new Map(leaderboard.map((e) => [e.guest.id, e.guest]));
  const winners = Object.entries(lastPoints)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id, pts]) => ({ guest: byId.get(id), pts }))
    .filter((w) => w.guest);
  if (winners.length === 0) return <div className="mt-[4vh] text-[3vh] text-ink-dim">Personne n’a trouvé ! 😱</div>;
  return (
    <ol className="mt-[4vh] space-y-[1.2vh]">
      {winners.map((w, i) => (
        <li key={w.guest!.id} className="flex items-center gap-[1vw] text-[3vh]">
          <span className="w-[3vw]">{i === 0 ? '⚡' : ''}</span>
          <Avatar guest={w.guest!} className="!h-[5vh] !w-[5vh] !text-[3vh]" />
          <span className="flex-1 font-semibold">{w.guest!.pseudo}</span>
          <span className="font-bold text-lime">+{w.pts}</span>
        </li>
      ))}
    </ol>
  );
}

export function Podium({ board }: { board: Board }) {
  const order = [1, 0, 2];
  const heights = ['h-[30vh]', 'h-[22vh]', 'h-[16vh]'];
  return (
    <div className="flex flex-1 flex-col items-center justify-end pb-[4vh]">
      <div className="font-display mb-[6vh] text-[7vh] font-bold">🏆 Le podium</div>
      <div className="flex items-end gap-[2vw]">
        {order.map((rank) => {
          const e = board[rank];
          if (!e) return <div key={rank} className="w-[18vw]" />;
          return (
            <div key={rank} className="flex w-[18vw] flex-col items-center">
              <Avatar guest={e.guest} className="!h-[12vh] !w-[12vh] !text-[7vh]" />
              <div className="mt-[1vh] max-w-full truncate text-[3.4vh] font-bold">{e.guest.pseudo}</div>
              <div className="text-[2.6vh] text-ink-dim">{e.points} pts</div>
              <div className={cn('bg-gradient-neon mt-[1.5vh] flex w-full items-start justify-center rounded-t-[2vh] pt-[1.5vh] text-[7vh]', heights[rank])}>
                {['🥇', '🥈', '🥉'][rank]}
              </div>
            </div>
          );
        })}
      </div>
      {board.length === 0 && <div className="text-[3vh] text-ink-dim">Pas de points cette fois-ci !</div>}
    </div>
  );
}
